import { ActionIcon, type ActionIconProps, Tooltip, type TooltipProps } from '@mantine/core';
import type { ReactNode } from 'react';

export interface IconActionButtonProps extends Omit<ActionIconProps, 'onClick' | 'aria-label' | 'children'> {
  /** Visible tooltip on hover. Also drives `aria-label` unless `ariaLabel` overrides. */
  readonly label: string;
  /** Optional override for the accessible name — use when the visual tooltip
   * needs to change with action state (e.g. flips to "Copied" after copy)
   * but the accessible name should stay stable. */
  readonly ariaLabel?: string;
  readonly onClick: () => void;
  readonly tooltipPosition?: TooltipProps['position'];
  /** Icon element — caller chooses size via the tabler `size` prop. */
  readonly children: ReactNode;
}

/**
 * SSOT for "icon-only button with tooltip and accessible name". Wraps an
 * `<ActionIcon>` in a `<Tooltip>` and binds `aria-label` so role+name
 * queries and screen readers both get a usable accessible name.
 *
 * Compose this for specialised buttons (e.g. `DownloadButton`) rather
 * than re-creating the wrap.
 */
export function IconActionButton({
  label,
  ariaLabel,
  onClick,
  tooltipPosition,
  children,
  ...actionIconProps
}: IconActionButtonProps) {
  return (
    <Tooltip label={label} position={tooltipPosition} withArrow>
      <ActionIcon aria-label={ariaLabel ?? label} onClick={onClick} {...actionIconProps}>
        {children}
      </ActionIcon>
    </Tooltip>
  );
}
